import { Modal } from "bootstrap"
import StepController, { StepError } from "../../../lib/config_tool_step"
import { postJson } from "../../../lib/json_request"
import { readUidStm32wb5x } from "../../../lib/chips/stm32wb5x"

// serial_test: führt den HW-Test auf der Platine des Laufs aus (dmct.md #26, Protokoll in
// ../oxobutton-worktrees/hw-test/docs/bench_tool_protocol.md). Ablauf: Port aus den Einstellungen öffnen (oder die
// Verbindung des Terminals nutzen, wenn sie auf demselben Port mit 115200 läuft) → Platine über SWD ohne Neuverbindung
// zurücksetzen, damit der Test von vorn beginnt, während wir schon zuhören (das Gerät fragt <KC> gleich nach dem Start
// und verwirft alles, was vor einer Anfrage kommt) → Zeilen anzeigen und Anfragen beantworten → das Urteil beendet
// den Schritt. Anfragen und Urteil zählen erst nach einem <TI>, das nach dem Abonnieren kommt; Zeilen davor stammen
// aus einem Lauf, der schon vorher lief.
//
// Antworten: <KC> = Prüfwert aus prepare_chip; <PI> und <LA> fragen den Server (Fehler = leere Zeile, die Platine
// verweigert dann); vor <LA> wird die Chip-UID ohne Reset neu gelesen, damit die LoRa-Schlüssel sicher an die angemeldete
// Platine gehen (<KC> prüft nur 16 Bit). Die <LA>-Antwort enthält den AppKey und erscheint nie im Protokoll.
// <RQ> und <DP> öffnen das Popup (Bootstrap-Modal), <CP> schliesst es. Kein Gesamt-Timeout (<RQ> darf Minuten dauern):
// Abbrechen beendet den Schritt, ein geschlossener Port ebenso.
const BAUD_RATE = 115200
const MAX_LINES = 1000
const UID_READERS = { stm32wb5x: readUidStm32wb5x }
// Urteilszeilen des HW-Tests (Anfang des Texts nach dem Tag; die Firmware hängt bei Fehlern „Reset device to repeat
// the test." an): true = bestanden.
const VERDICTS = { "HW Test passed!": true, "HW Test failed!": false, "Provisioning failed!": false }
const TAG_CLASSES = { TI: "fw-bold", SC: "text-success", WN: "text-warning", ER: "text-danger", RQ: "text-info" }

export default class extends StepController {
  static targets = ["log", "cancel", "popup", "popupText", "popupAnswers"]
  static values = { mcu: String, texts: Object }

  #session = null
  #modal = null

  disconnect() {
    this.#session?.fail(this.#error("cancelled"))
    this.#modal?.dispose()
  }

  reset() {
    super.reset()
    this.#session?.fail(this.#error("cancelled"))
    this.logTarget.replaceChildren()
    this.logTarget.hidden = true
    this.#closePopup()
  }

  async perform(run) {
    this.reset()
    try {
      if (!run.board) throw this.#error("no_board")
      const readUid = UID_READERS[this.mcuValue]
      if (!readUid) throw this.#error("unsupported_mcu", { mcu: this.mcuValue })

      const closePort = await this.#openPort()
      try {
        await this.#runTest(run, readUid)
      } finally {
        if (closePort) await this.bridge.serial.close().catch(() => {})
      }
      this.status(this.#say("passed"))
    } catch (error) {
      this.failed(error)
    }
  }

  cancel() {
    this.#session?.fail(this.#error("cancelled"))
  }

  answerYes() {
    this.#answer("y")
  }

  answerNo() {
    this.#answer("n")
  }

  // Öffnet den Port aus den Einstellungen. true = wir haben ihn geöffnet und schliessen ihn am Ende wieder.
  async #openPort() {
    const { serialPort } = await this.bridge.settings.get()
    if (!serialPort) throw this.#error("no_port")

    const current = await this.bridge.serial.status()
    if (current.open) {
      if (current.path !== serialPort) throw this.#error("port_busy", { path: current.path, port: serialPort })
      if (current.baudRate !== BAUD_RATE) throw this.#error("wrong_baud", { baud: current.baudRate })
      return false
    }
    this.status(this.#say("opening", { port: serialPort }))
    await this.bridge.serial.open({ path: serialPort, baudRate: BAUD_RATE })
    return true
  }

  #runTest(run, readUid) {
    return new Promise((resolve, reject) => {
      let armed = false
      let queue = Promise.resolve()
      const unsubscribe = []
      const session = {
        finish: () => {
          cleanup()
          resolve()
        },
        fail: (error) => {
          cleanup()
          reject(error)
        },
      }
      const cleanup = () => {
        unsubscribe.forEach((stop) => stop())
        if (this.#session === session) this.#session = null
        this.cancelTarget.hidden = true
        this.#closePopup()
      }
      this.#session = session

      // Zeilen strikt nacheinander, damit Antworten und Protokoll in der Reihenfolge des Geräts bleiben.
      unsubscribe.push(
        this.bridge.serial.onLine((raw) => {
          queue = queue
            .then(async () => {
              if (this.#session !== session) return
              const line = raw.replace(/\r?\n$/, "")
              const [, tag, message = line] = line.match(/^<([A-Z]{2})>(.*)$/) ?? []
              this.#log(tag === "LF" ? "" : message.trim(), TAG_CLASSES[tag] ?? (tag ? null : "text-body-secondary"))
              if (tag === "TI") armed = true
              if (armed && tag) await this.#handle(tag, message, run, readUid, session)
            })
            .catch((error) => session.fail(error))
        }),
      )
      unsubscribe.push(
        this.bridge.serial.onStatus((status) => {
          if (!status.open) session.fail(this.#error("port_closed", { error: status.error ?? "" }))
        }),
      )

      this.cancelTarget.hidden = false
      this.status(this.#say("resetting"))
      this.bridge.stlink
        .exec({ args: ["-rst"], mode: "HOTPLUG" })
        .then(() => this.#session === session && this.status(this.#say("running")))
        .catch((error) => session.fail(error))
    })
  }

  async #handle(tag, message, run, readUid, session) {
    const boardUrl = `${run.boardsUrlValue}/${run.board.mcu_uid}`
    switch (tag) {
      case "KC":
        return this.#reply(run.board.key_check)
      case "PI": {
        const [hw_id = "", hw_version = ""] = message.trim().split(",")
        const data = await this.#ask(() => postJson(`${boardUrl}/production_info`, { hw_id, hw_version }))
        return this.#reply(data?.reply ?? "")
      }
      case "LA": {
        const data = await this.#ask(async () => {
          const uid = await readUid(this.bridge, "HOTPLUG")
          if (uid !== run.board.mcu_uid) throw this.#error("uid_mismatch", { uid, board: run.board.mcu_uid })
          return postJson(`${boardUrl}/lora_keys`)
        })
        if (!data) return this.#reply("")
        run.board = { ...run.board, deveui: data.deveui }
        return this.#reply(data.reply, this.#say("lora_keys_sent", { deveui: data.deveui }))
      }
      case "LT":
        return this.#reply("")
      case "RQ":
        return this.#openPopup(message.trim(), true)
      case "DP":
        return this.#openPopup(message.trim(), false)
      case "CP":
        return this.#closePopup()
      case "SC":
      case "ER": {
        const verdict = Object.entries(VERDICTS).find(([text]) => message.trim().startsWith(text))?.[1]
        if (verdict === true) session.finish()
        else if (verdict === false) session.fail(new StepError("hw_test_failed", this.#say("failed", { verdict: message.trim() })))
      }
    }
  }

  // Fragt den Server für eine Antwort. Bei einem Fehler null und der Grund rot ins Protokoll; der Aufrufer verweigert
  // dann mit einer leeren Zeile.
  async #ask(request) {
    try {
      return await request()
    } catch (error) {
      this.#log(error.message, "text-danger")
      return null
    }
  }

  // `shown` ersetzt die Antwort im Protokoll (<LA>: der AppKey erscheint nie).
  async #reply(text, shown = null) {
    this.#log(`→ ${shown ?? (text || this.#say("refused"))}`, "text-primary")
    await this.bridge.serial.write(`${text}\n`)
  }

  async #answer(char) {
    if (!this.#session) return
    this.#closePopup()
    try {
      await this.#reply(char)
    } catch (error) {
      this.#session?.fail(error)
    }
  }

  #openPopup(text, question) {
    this.popupTextTarget.textContent = text
    this.popupAnswersTarget.hidden = !question
    this.#modal ??= new Modal(this.popupTarget, { backdrop: "static", keyboard: false })
    this.#modal.show()
  }

  #closePopup() {
    this.#modal?.hide()
  }

  #log(text, className) {
    const line = document.createElement("div")
    line.textContent = text
    if (className) line.className = className
    const log = this.logTarget
    log.hidden = false
    const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 8
    log.appendChild(line)
    while (log.childElementCount > MAX_LINES) log.firstElementChild.remove()
    if (atBottom) log.scrollTop = log.scrollHeight
  }

  // Protokoll für das Produktionsprotokoll: die Zeilen des Geräts und unsere Antworten (ohne AppKey, siehe #reply).
  get logLines() {
    return [...this.logTarget.children].map((line) => line.textContent)
  }

  #error(kind, vars = {}) {
    return new StepError(kind, this.#say(kind, vars))
  }

  #say(key, vars = {}) {
    return Object.entries(vars).reduce((text, [name, value]) => text.replaceAll(`%{${name}}`, value), this.textsValue[key] ?? key)
  }
}
